/**
 * Selection strategy accepted by the TLF bundle export endpoint.
 */
export type TlfBundleSelectionMode = "All" | "Only" | "AllExcept";

/**
 * Configured maximum compressed payload size for one bundle upload chunk.
 */
export const TLF_BUNDLE_CHUNK_SIZE_BYTES = 1_500_000;

/**
 * Number of upload chunk requests sent concurrently in one batch.
 */
export const TLF_BUNDLE_UPLOAD_BATCH_SIZE = 3;

/**
 * Request body used to create a hierarchy TLF bundle export.
 */
export interface TlfBundleExportRequest {
  hierarchyId: string;
  mode: TlfBundleSelectionMode;
  items: string[];
  includeComments: boolean;
  includeDescendants: boolean;
  includeParentHierarchyArsMetadata: boolean;
  exportOnlyReferencedMetadata: boolean;
  ignoreRetiredAndDecommissioned: boolean;
}

/**
 * Export metadata returned before bundle chunks can be downloaded.
 */
export interface TlfBundleExportResponse {
  exportId: string;
  totalChunks: number;
  totalBytes: number;
  sha256: string;
  itemCount: number;
}

/**
 * Parameters identifying a chunk in a prepared export.
 */
export interface TlfBundleExportChunkRequest {
  hierarchyId: string;
  exportId: string;
  sequenceNumber: number;
}

/**
 * Parameters identifying an export to delete after transfer.
 */
export interface TlfBundleDeleteExportRequest {
  hierarchyId: string;
  exportId: string;
}

/**
 * Parameters and chunk data for one upload request.
 */
export interface TlfBundleUploadChunkRequest {
  hierarchyId: string;
  uploadId: string;
  sequenceNumber: number;
  totalChunks: number;
  chunk: Blob;
}

/**
 * Request body used to finalise an uploaded compressed bundle.
 */
export interface TlfBundleCompleteUploadRequest {
  hierarchyId: string;
  uploadId: string;
  totalBytes: number;
  sha256: string;
}

/**
 * Upload metadata returned after all chunks have been accepted.
 */
export interface TlfBundleCompleteUploadResponse {
  uploadId: string;
  totalBytes: number;
  sha256: string;
  itemCount: number;
}

/**
 * Request body used to analyse a completed upload.
 */
export interface TlfBundleAnalyzeRequest {
  hierarchyId: string;
  uploadId: string;
}

/**
 * Summary returned by bundle analysis.
 */
export interface TlfBundleAnalysisResponse {
  analysisId: string;
  fingerprint: string;
  itemCount: number;
  newItemCount: number;
  identicalItemCount: number;
  conflictCount: number;
  itemConflictCount: number;
  dependencyConflictCount: number;
  reportsToCreate: string[];
}

/**
 * Parameters for one page of analysed bundle conflicts.
 */
export interface TlfBundleConflictPageRequest {
  hierarchyId: string;
  analysisId: string;
  pageNumber: number;
  pageSize: number;
}

/**
 * Resolution that can be applied to a bundle conflict.
 */
export type TlfBundleConflictResolution = "Replace" | "Ignore";

/**
 * Conflict returned by bundle analysis.
 */
export interface TlfBundleConflict {
  conflictId: string;
  kind: string;
  componentKind: string | null;
  source: Record<string, unknown>;
  target: Record<string, unknown>;
  allowedResolutions: TlfBundleConflictResolution[];
  defaultResolution: TlfBundleConflictResolution;
  affectedItems: unknown[];
  hasDiff: boolean;
}

/**
 * Page metadata and conflict rows returned by the analysis API.
 */
export interface TlfBundleConflictPageResponse {
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  items: TlfBundleConflict[];
}

/**
 * Parameters identifying a conflict whose field differences are requested.
 */
export interface TlfBundleConflictDiffRequest {
  hierarchyId: string;
  analysisId: string;
  conflictId: string;
}

/**
 * One field-level difference between a source and target item.
 */
export interface TlfBundleConflictDiff {
  path: string;
  sourceValue: unknown;
  targetValue: unknown;
}

/**
 * Request body used to start an import from an analysed upload.
 */
export interface TlfBundleImportRequest {
  hierarchyId: string;
  uploadId: string;
  analysisId: string;
  defaultResolutionByKind: Record<string, TlfBundleConflictResolution>;
  overrides: Record<string, TlfBundleConflictResolution>;
}

/**
 * Job identifier returned after an import is accepted.
 */
export interface TlfBundleImportAcceptedResponse {
  jobId: string;
}

/**
 * Current state and progress of a background import job.
 */
export interface TlfBundleImportJobStatus {
  jobId: string;
  status:
    | "Queued"
    | "Running"
    | "CancelRequested"
    | "Cancelled"
    | "Completed"
    | "CompletedWithFailures"
    | "Failed";
  phase: string | null;
  total: number;
  processed: number;
  remaining: number;
  totalTlfItems: number;
  processedTlfItems: number;
  totalHierarchyMetadata: number;
  processedHierarchyMetadata: number;
  succeeded: number;
  skipped: number;
  failed: number;
  error: string | null;
  startedAt: string | null;
  finishedAt: string | null;
}

/**
 * One item failure returned by a paged import failure request.
 */
export interface TlfBundleImportFailure {
  sequenceNumber: number;
  kind: string;
  sourceKey: string;
  error: string;
}

/**
 * Page of item-level failures for a background import job.
 */
export interface TlfBundleImportFailurePage {
  skip: number;
  take: number;
  totalCount: number;
  items: TlfBundleImportFailure[];
}

/**
 * Parameters for loading an import job or its failures.
 */
export interface TlfBundleImportJobRequest {
  hierarchyId: string;
  jobId: string;
}

/**
 * Parameters for loading one page of import failures.
 */
export interface TlfBundleImportFailuresRequest extends TlfBundleImportJobRequest {
  skip: number;
  take: number;
}

/**
 * Progress counters for a chunked TLF bundle transfer.
 */
export interface TlfBundleTransferProgress {
  completedChunks: number;
  totalChunks: number;
  transferredBytes: number;
  totalBytes: number;
}

/**
 * Stage reported while uploading and analysing a TLF bundle.
 */
export type TlfBundleImportStage = "compressing" | "uploading" | "completing" | "analysing";

/**
 * Progress reported by the upload and analysis workflow.
 */
export interface TlfBundleImportProgress extends TlfBundleTransferProgress {
  stage: TlfBundleImportStage;
  currentChunkPercent: number;
}

/**
 * Completed upload and its target hierarchy analysis.
 */
export interface TlfBundleUploadAnalysis {
  uploadId: string;
  compressedBytes: number;
  analysis: TlfBundleAnalysisResponse;
}
